import React, { useEffect, useState, useCallback } from "react";
import { Folder, FolderPlus, Pencil, Trash2, Inbox, Download, Filter } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ImageCard from "@/components/ImageCard";
import FolderPicker from "@/components/FolderPicker";
import Lightbox from "@/components/Lightbox";

export default function Library() {
  const [folders, setFolders] = useState([]);
  const [demographics, setDemographics] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [activeFolder, setActiveFolder] = useState("all");
  const [demoFilter, setDemoFilter] = useState("all");
  const [langFilter, setLangFilter] = useState("all");
  const [images, setImages] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [moveTarget, setMoveTarget] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [viewImage, setViewImage] = useState(null);

  const loadFolders = useCallback(async () => {
    const { data } = await api.get("/folders");
    setFolders(data);
  }, []);

  const loadImages = useCallback(async () => {
    const params = new URLSearchParams();
    if (activeFolder === "unfiled") params.set("unfiled", "true");
    else if (activeFolder !== "all") params.set("folder_id", activeFolder);
    if (demoFilter && demoFilter !== "all") params.set("demographic", demoFilter);
    if (langFilter && langFilter !== "all") params.set("language", langFilter);
    const { data } = await api.get(`/images?${params.toString()}`);
    setImages(data);
  }, [activeFolder, demoFilter, langFilter]);

  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { loadImages(); }, [loadImages]);
  useEffect(() => {
    api.get("/styles").then((r) => {
      setDemographics(r.data.demographics || []);
      setLanguages((r.data.languages || []).filter(l => l.code !== "auto"));
    }).catch(() => {});
  }, []);

  const create = async () => {
    if (!newFolderName.trim()) return;
    try {
      const { data } = await api.post("/folders", { name: newFolderName.trim() });
      setFolders([data, ...folders]);
      setNewFolderName("");
      setCreating(false);
      toast.success("Folder created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const rename = async () => {
    if (!renameValue.trim()) return;
    try {
      const { data } = await api.patch(`/folders/${renaming.id}`, { name: renameValue.trim() });
      setFolders(folders.map((f) => (f.id === data.id ? data : f)));
      setRenaming(null);
      toast.success("Renamed");
    } catch {
      toast.error("Rename failed");
    }
  };

  const removeFolder = async (folder) => {
    if (!confirm(`Delete folder "${folder.name}"? Images will move to Unfiled.`)) return;
    try {
      await api.delete(`/folders/${folder.id}`);
      setFolders(folders.filter((f) => f.id !== folder.id));
      if (activeFolder === folder.id) setActiveFolder("all");
      toast.success("Folder deleted");
    } catch {
      toast.error("Failed");
    }
  };

  const downloadOne = async (img) => {
    try {
      const resp = await fetch(fileUrl(img.url));
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = img.filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed"); }
  };

  const downloadFolder = async () => {
    if (images.length === 0) return;
    try {
      const resp = await api.post("/images/zip", { ids: images.map((i) => i.id) }, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/zip" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `library-${activeFolder}-${Date.now()}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("ZIP failed"); }
  };

  const onMove = (img) => { setMoveTarget(img); setShowPicker(true); };

  const moveTo = async (folderId) => {
    try {
      await api.patch(`/images/${moveTarget.id}/move`, { folder_id: folderId });
      setShowPicker(false); setMoveTarget(null);
      toast.success("Moved");
      loadImages(); loadFolders();
    } catch { toast.error("Failed"); }
  };

  const onDelete = async (img) => {
    if (!confirm("Delete this image?")) return;
    try {
      await api.delete(`/images/${img.id}`);
      setImages(images.filter((i) => i.id !== img.id));
      setViewImage(null);
      loadFolders();
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="flex h-full" data-testid="library-page">
      {/* Folder sidebar */}
      <aside className="w-64 border-r border-zinc-200 bg-zinc-50 overflow-y-auto flex-shrink-0">
        <div className="p-4 border-b border-zinc-200">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Folders</div>
            <button
              onClick={() => setCreating(true)}
              className="text-zinc-500 hover:text-zinc-900"
              data-testid="library-new-folder-button"
            >
              <FolderPlus className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
          {creating && (
            <div className="flex gap-1 mb-2">
              <Input
                autoFocus
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && create()}
                placeholder="Name" className="rounded-sm h-8 text-sm"
                data-testid="library-new-folder-input"
              />
              <Button size="sm" onClick={create} className="rounded-sm h-8 bg-[#0055FF] hover:bg-[#0040CC]" data-testid="library-create-folder">
                Add
              </Button>
            </div>
          )}
        </div>

        <nav className="p-2 space-y-0.5">
          {[
            { key: "all", label: "All images", icon: Inbox },
            { key: "unfiled", label: "Unfiled", icon: Inbox },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveFolder(key)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                activeFolder === key ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100"
              }`}
              data-testid={`folder-tab-${key}`}
            >
              <Icon className="w-4 h-4" strokeWidth={1.75} />
              <span className="flex-1 text-left">{label}</span>
            </button>
          ))}
          <div className="border-t border-zinc-200 my-2" />
          {folders.map((f) => (
            <div
              key={f.id}
              className={`flex items-center gap-1 rounded-sm group ${
                activeFolder === f.id ? "bg-zinc-900" : "hover:bg-zinc-100"
              }`}
            >
              <button
                onClick={() => setActiveFolder(f.id)}
                className={`flex-1 flex items-center gap-3 px-3 py-2 text-sm text-left ${
                  activeFolder === f.id ? "text-white" : "text-zinc-700"
                }`}
                data-testid={`folder-tab-${f.id}`}
              >
                <Folder className="w-4 h-4" strokeWidth={1.75} />
                <span className="flex-1 truncate">{f.name}</span>
                <span className="font-mono text-[10px] opacity-60">{f.image_count}</span>
              </button>
              <div className="hidden group-hover:flex pr-2 gap-1">
                <button
                  onClick={() => { setRenaming(f); setRenameValue(f.name); }}
                  className={activeFolder === f.id ? "text-white/80 hover:text-white" : "text-zinc-500 hover:text-zinc-900"}
                  data-testid={`rename-folder-${f.id}`}
                >
                  <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                </button>
                <button
                  onClick={() => removeFolder(f)}
                  className={activeFolder === f.id ? "text-white/80 hover:text-white" : "text-zinc-500 hover:text-red-600"}
                  data-testid={`delete-folder-${f.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                </button>
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main */}
      <section className="flex-1 overflow-y-auto bg-white">
        <div className="p-6 lg:p-8">
          <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Library</div>
              <h2 className="font-heading text-2xl font-bold text-zinc-900 mt-1">
                {activeFolder === "all" && "All images"}
                {activeFolder === "unfiled" && "Unfiled"}
                {activeFolder !== "all" && activeFolder !== "unfiled" &&
                  (folders.find((f) => f.id === activeFolder)?.name || "Folder")}
              </h2>
              <p className="text-sm text-zinc-500 mt-1">{images.length} image{images.length === 1 ? "" : "s"}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-4 h-4 text-zinc-400" strokeWidth={1.75} />
              <Select value={langFilter} onValueChange={setLangFilter}>
                <SelectTrigger className="w-[160px] rounded-sm border-zinc-200" data-testid="lang-filter-select">
                  <SelectValue placeholder="All languages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All languages</SelectItem>
                  {languages.map((l) => (
                    <SelectItem key={l.code} value={l.code}>{l.native}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={demoFilter} onValueChange={setDemoFilter}>
                <SelectTrigger className="w-[200px] rounded-sm border-zinc-200" data-testid="demo-filter-select">
                  <SelectValue placeholder="All demographics" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All demographics</SelectItem>
                  {demographics.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {images.length > 0 && (
                <Button onClick={downloadFolder} variant="outline" className="rounded-sm" data-testid="library-download-all">
                  <Download className="w-4 h-4 mr-2" /> Download ZIP
                </Button>
              )}
            </div>
          </div>

          {images.length === 0 ? (
            <div className="border border-dashed border-zinc-200 rounded-sm p-16 text-center" data-testid="library-empty">
              <Inbox className="w-10 h-10 mx-auto text-zinc-300" strokeWidth={1.5} />
              <h3 className="mt-4 font-heading text-lg font-semibold text-zinc-900">No images here yet</h3>
              <p className="mt-2 text-sm text-zinc-500">Generate some on the Generator page or move images here.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {images.map((img) => (
                <ImageCard
                  key={img.id}
                  status="ok"
                  image={img}
                  onDownload={downloadOne}
                  onMove={onMove}
                  onDelete={onDelete}
                  onView={(i) => setViewImage(i)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Rename dialog */}
      <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
        <DialogContent className="rounded-sm" data-testid="rename-folder-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Rename folder</DialogTitle>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            className="rounded-sm"
            data-testid="rename-folder-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenaming(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={rename} className="rounded-sm bg-[#0055FF] hover:bg-[#0040CC]" data-testid="rename-folder-confirm">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FolderPicker
        open={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={moveTo}
        currentFolderId={moveTarget?.folder_id}
      />
      <Lightbox
        open={!!viewImage}
        onClose={() => setViewImage(null)}
        image={viewImage}
        onDownload={downloadOne}
        onMove={(img) => { setViewImage(null); onMove(img); }}
        onDelete={onDelete}
      />
    </div>
  );
}
